import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ErrorBoundary, FDProvider, ToastProvider } from "@fd/ui";
// Self-hosted brand fonts (Inter for UI, JetBrains Mono for IDs/amounts) — family
// names "Inter" / "JetBrains Mono" match tokens.css exactly.
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/600.css";
import "@fd/tokens/css";
import "@fd/ui/css";
// Initialize i18n (language detection + <html lang> sync) before first render so
// the auth gate and every screen see the resolved language on the first paint.
import "./i18n";
import { restoreCustomerSession } from "./lib/customerSession";
import { isSupportHost } from "./lib/portalPlatform";
import i18n from "./i18n";
import { App } from "./App";
import { captureAdminViewFromUrl } from "./lib/adminView";
import { capturePortalFromUrl } from "./lib/portalSession";
import { captureSupportStaffViewFromUrl } from './lib/supportStaffView';
import { setCatalog, type Catalog, setCatalogEntitlements, type CatalogEntitlements } from "@fd/pricing";

// Capture an admin "view as customer" grant from the URL fragment before first render,
// so the auth gate + apiFetch see it on the very first call.
captureAdminViewFromUrl();
captureSupportStaffViewFromUrl();
// Likewise capture the passwordless portal-login marker (#portal=1) set by the magic-
// link verify redirect, so the auth gate treats the browser as signed in on first paint.
capturePortalFromUrl();

// Hydrate the pricing engine from the live published catalog (/api/public/catalog) before
// first render, so the quote builder reflects the latest published prices with NO redeploy.
// Bounded by a short timeout; on a slow/failed fetch we keep the bundled snapshot (pricing
// still works, just possibly a cache-window stale). See packages/pricing setCatalog().
async function hydrateCatalog(): Promise<void> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2500);
    const res = await fetch("/api/public/catalog", { headers: { Accept: "application/json" }, signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return;
    const data = (await res.json()) as { catalog?: Catalog; entitlements?: CatalogEntitlements | null };
    if (data.catalog) setCatalog(data.catalog);
    // Entitlements ride beside the snapshot (not inside it). No bundled fallback —
    // panels stay hidden until this hydrates.
    if (data.entitlements) setCatalogEntitlements(data.entitlements);
  } catch {
    /* keep the bundled fallback */
  }
}

const selectedLanguage = new URLSearchParams(window.location.search).get('lang');
if (selectedLanguage === 'en' || selectedLanguage === 'de') void i18n.changeLanguage(selectedLanguage);
// Public help must render even when account/session services are unavailable.
const publicHelp = window.location.pathname === '/support' || /^\/support\/articles\//.test(window.location.pathname)
  || (isSupportHost() && window.location.pathname === '/');
const needsSession = !publicHelp && (/^\/(app|support|servicedesk)(\/|$)/.test(window.location.pathname) || window.location.pathname === '/');
void Promise.allSettled([
  isSupportHost() || publicHelp ? Promise.resolve() : hydrateCatalog(),
  needsSession ? restoreCustomerSession() : Promise.resolve(),
]).finally(() => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <FDProvider theme="light">
        <ToastProvider>
          <ErrorBoundary>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ErrorBoundary>
        </ToastProvider>
      </FDProvider>
    </React.StrictMode>
  );
});
